import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Shirt,
  Sparkles,
  Droplets,
  Truck,
  Clock,
  ChevronRight,
  Check,
  Snowflake,
  Waves,
} from "lucide-react";

const ServicesPage = () => {
  const washerPrices = [
    { size: "10lb Top Loaders", price: "$4.50" },
    { size: "20lb Front Loaders", price: "$5.50" },
    { size: "30lb Heavy Duty Front Loaders", price: "$8.00" },
    { size: "40lb Heavy Duty Front Loaders", price: "$10.00" },
    { size: "60lb Heavy Duty Front Loaders", price: "$13.00" },
  ];

  const services = [
    {
      id: "self-serve",
      icon: Waves,
      title: "Self-Serve Laundry",
      description: "Our state-of-the-art facility features various sizes of washers and dryers for all your laundry needs.",
      features: [
        "Under live camera surveillance",
        "Various washer sizes (10lb - 60lb)",
        "High-efficiency machines",
        "Soft water for better cleaning",
        "Change machine available",
      ],
      cta: "Visit Us",
      link: "/contact",
    },
    {
      id: "wash-fold",
      icon: Shirt,
      title: "Wash & Fold Service",
      description: "Let us handle your laundry. Drop off and we'll have it cleaned, folded, and ready for pickup.",
      features: [
        "$2.59 per pound (15 lb minimum)",
        "Same day service available (+$30)",
        "Drop off before 12pm for same day",
        "Must be sorted and pre-treated",
        "Delivery available",
      ],
      highlight: true,
      cta: "Book Now",
      link: "/contact",
    },
    {
      id: "dry-cleaning",
      icon: Sparkles,
      title: "Dry Cleaning",
      description: "Professional eco-friendly dry cleaning service for your delicate garments and special items.",
      features: [
        "Eco-friendly cleaning process",
        "Fast turnaround",
        "Expert stain removal",
        "Gentle on fabrics",
        "Professional pressing",
      ],
      highlight: true,
      cta: "Book Service",
      link: "/contact",
    },
    {
      id: "fresh-gear",
      icon: Snowflake,
      title: "Fresh Gear Ozone Sanitization",
      description: "Our top-of-the-line, environmentally friendly, chemical-free ozone sanitization unit.",
      features: [
        "Perfect for hockey gear",
        "Leather jackets & bags",
        "Sports equipment",
        "Chemical-free process",
        "Eliminates odors completely",
      ],
      cta: "Learn More",
      link: "/freshgear",
    },
    {
      id: "water-refills",
      icon: Droplets,
      title: "Water Refills",
      description: "Purified water refill station with bottle sanitization feature.",
      features: [
        "Purified drinking water",
        "Bottle sanitization included",
        "Water plans available",
        "Bottle sales on-site",
        "Safe & hygienic",
      ],
      cta: "Learn More",
      link: "/water",
    },
    {
      id: "pickup-delivery",
      icon: Truck,
      title: "Pickup & Delivery",
      description: "We pick up, clean, and deliver directly to your door within our 25km service area.",
      features: [
        "Free within 25km radius",
        "Convenient scheduling",
        "Track your order",
        "Same-day available",
        "Contactless option",
      ],
      highlight: true,
      cta: "Schedule Pickup",
      link: "/booking",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-white via-sky-50 to-sky-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">Our Services</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mt-3">
              Professional Services for Your Clothes
            </h1>
            <p className="text-slate-600 mt-6 text-lg">
              Ranked excellent by our customers in every category. From self-serve to full-service, 
              we've got all your laundry needs covered.
            </p>
          </div>
        </div>
      </section>

      {/* Special Offers */}
      <section className="py-12 bg-sky-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 text-center text-white">
            <div className="p-6">
              <Clock className="w-8 h-8 mx-auto mb-3 text-sky-200" />
              <p className="font-semibold text-lg">Flexible Payment Solutions</p>
              <p className="text-sky-100">Cash and Cashless payments accepted</p>
            </div>
            <div className="p-6 border-x border-sky-500">
              <Sparkles className="w-8 h-8 mx-auto mb-3 text-sky-200" />
              <p className="font-semibold text-lg">Loyalty Reward</p>
              <p className="text-sky-100">Every 11th wash FREE!</p>
            </div>
            <div className="p-6">
              <Truck className="w-8 h-8 mx-auto mb-3 text-sky-200" />
              <p className="font-semibold text-lg">Pickup & Delivery</p>
              <p className="text-sky-100">We deliver to your door</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card 
                key={service.id}
                id={service.id}
                className={`service-card h-full ${
                  service.highlight ? "ring-2 ring-sky-500 ring-offset-2" : "border-slate-100"
                }`}
                data-testid={`service-${service.id}`}
              >
                <CardHeader className="pb-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${
                    service.highlight 
                      ? "bg-gradient-to-br from-sky-500 to-sky-600 text-white" 
                      : "bg-sky-50 text-sky-600"
                  }`}>
                    <service.icon className="w-7 h-7" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link}>
                    <Button 
                      className={`w-full rounded-full mt-4 ${
                        service.highlight 
                          ? "bg-sky-600 hover:bg-sky-700" 
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                      data-testid={`service-${service.id}-btn`}
                    >
                      {service.cta}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tables */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-sky-600 font-medium text-sm uppercase tracking-wider">Pricing</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3">
              Transparent Pricing
            </h2>
            <p className="text-slate-600 mt-4">
              Our self-serve machines accept quarters, loonies, toonies and cashless payments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Washers */}
            <Card className="border-slate-100" data-testid="washer-prices">
              <CardHeader className="bg-gradient-to-r from-sky-600 to-sky-700 text-white rounded-t-lg">
                <CardTitle className="text-center">Washers</CardTitle>
                <p className="text-center text-sky-100 text-sm">Accepts quarters, loonies, toonies, phone payments</p>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <tbody>
                    {washerPrices.map((item, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                        <td className="px-6 py-4 text-slate-700">{item.size}</td>
                        <td className="px-6 py-4 text-right font-semibold text-sky-600">{item.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Dryers & Wash/Fold */}
            <div className="space-y-8">
              <Card className="border-slate-100" data-testid="dryer-prices">
                <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-t-lg">
                  <CardTitle className="text-center">Dryers</CardTitle>
                  <p className="text-center text-slate-300 text-sm">Accepts quarters and phone payments</p>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">30lb Dryers</span>
                    <span className="font-semibold text-sky-600">$0.25 / 2.5 mins</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-sky-200 bg-sky-50" data-testid="washfold-prices">
                <CardHeader>
                  <CardTitle className="text-sky-800">Wash & Fold</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">Per pound</span>
                    <span className="font-semibold text-sky-600">$2.59</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">Minimum order</span>
                    <span className="font-semibold text-slate-600">15 lbs</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">Same day service</span>
                    <span className="font-semibold text-slate-600">+$30.00</span>
                  </div>
                  <p className="text-xs text-slate-500 pt-2">
                    * Same day: must be dropped off before 12pm. Must be sorted & pre-treated.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-600 to-sky-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Ready to Experience Our Services?
          </h2>
          <p className="text-sky-100 mt-4">
            Book a pickup or visit our location today.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/booking">
              <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8" data-testid="services-book-btn">
                Book a Pickup
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8" data-testid="services-contact-btn">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
